import { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp,
  Play,
  Loader2,
  AlertCircle,
  Download,
  ArrowUp,
  ArrowDown,
  Minus,
  BarChart3,
} from 'lucide-react';
import {
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  Area,
  AreaChart,
  ComposedChart,
  Bar,
} from 'recharts';
import clsx from 'clsx';
import type { ForecastResult, Entity } from '@/types';
import { forecastApi, entitiesApi } from '@/api/client';
import { useAppStore } from '@/store';

// --- Helpers -----------------------------------------------------------------

function formatAmount(amount: number): string {
  if (amount === 0) return '\u2014';
  const abs = Math.abs(amount);
  if (abs >= 1_000_000) return `${(amount / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(amount / 1_000).toFixed(1)}K`;
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(amount);
}

// --- Main Page ---------------------------------------------------------------

export default function Forecasting() {
  const { addNotification } = useAppStore();

  const [entities, setEntities] = useState<Entity[]>([]);
  const [forecasts, setForecasts] = useState<ForecastResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [selectedEntities, setSelectedEntities] = useState<string[]>([]);
  const [periodsAhead, setPeriodsAhead] = useState('6');
  const [method, setMethod] = useState('auto');

  // Comparison state
  const [comparisonData, setComparisonData] = useState<{
    forecast: ForecastResult;
    actuals: { period: string; value: number }[];
    accuracy_pct: number;
  } | null>(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [entitiesRes, forecastsRes] = await Promise.all([
        entitiesApi.list({ page: 1, page_size: 100 }),
        forecastApi.list(),
      ]);
      setEntities(entitiesRes.items);
      setForecasts(forecastsRes);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const results = await forecastApi.generate({
        entity_ids: selectedEntities.length > 0 ? selectedEntities : undefined,
        periods_ahead: parseInt(periodsAhead) || 6,
        method,
      });
      setForecasts(results);
      addNotification({ type: 'success', title: 'Forecast Generated', message: `${results.length} forecast(s) generated.` });
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Generation Failed', message: apiErr.detail || 'Failed to generate forecasts.' });
    } finally {
      setGenerating(false);
    }
  };

  const handleCompare = async (forecastId: string) => {
    try {
      const data = await forecastApi.compareActuals(forecastId);
      setComparisonData(data);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Comparison Failed', message: apiErr.detail || 'No actual data available yet.' });
    }
  };

  const handleExport = async () => {
    try {
      const blob = await forecastApi.export({ format: 'xlsx' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'forecast_report.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Export Failed', message: apiErr.detail || 'Failed to export.' });
    }
  };

  // Build chart data from first forecast result
  const primaryForecast = forecasts.length > 0 ? forecasts[0] : null;
  const chartData = primaryForecast
    ? primaryForecast.forecasted_values.map((fv) => ({
        period: fv.period,
        value: fv.value,
        lower: fv.lower_bound,
        upper: fv.upper_bound,
        band_80: [fv.lower_bound + (fv.value - fv.lower_bound) * 0.25, fv.upper_bound - (fv.upper_bound - fv.value) * 0.25],
      }))
    : [];

  // Build comparison chart data
  const compChartData = comparisonData
    ? comparisonData.forecast.forecasted_values.map((fv) => {
        const actual = comparisonData.actuals.find((a) => a.period === fv.period);
        return {
          period: fv.period,
          Forecast: fv.value,
          Actual: actual?.value ?? null,
        };
      })
    : [];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-blue-700" />
          </div>
          <div>
            <div className="skeleton w-56 h-7 mb-1" />
            <div className="skeleton w-72 h-4" />
          </div>
        </div>
        <div className="card p-6 animate-pulse">
          <div className="skeleton w-full h-80" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-blue-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Financial Forecasting</h1>
            <p className="text-sm text-gray-500 mt-0.5">AI-powered predictive analytics for financial planning</p>
          </div>
        </div>
        {forecasts.length > 0 && (
          <button onClick={handleExport} className="btn-secondary text-sm">
            <Download className="w-4 h-4 mr-1.5" />
            Export to XLSX
          </button>
        )}
      </div>

      {error && (
        <div className="card p-4 border-red-200 bg-red-50">
          <div className="flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
            <button onClick={loadData} className="ml-auto text-xs font-medium underline">Retry</button>
          </div>
        </div>
      )}

      {/* Generation Controls */}
      <div className="card p-5">
        <h2 className="text-base font-semibold text-gray-900 mb-4">Generate Forecast</h2>
        <div className="flex items-end gap-4 flex-wrap">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Entities</label>
            <select
              className="select-field text-sm w-56"
              multiple
              value={selectedEntities}
              onChange={(e) => setSelectedEntities(Array.from(e.target.selectedOptions, (o) => o.value))}
            >
              {entities.map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
            <p className="text-[10px] text-gray-400 mt-0.5">Leave empty for all entities</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Periods Ahead</label>
            <select className="select-field text-sm w-28" value={periodsAhead} onChange={(e) => setPeriodsAhead(e.target.value)}>
              <option value="3">3</option>
              <option value="6">6</option>
              <option value="12">12</option>
              <option value="24">24</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Method</label>
            <select className="select-field text-sm w-36" value={method} onChange={(e) => setMethod(e.target.value)}>
              <option value="auto">Auto (AI-Optimized)</option>
              <option value="linear">Linear Regression</option>
              <option value="exponential">Exponential Smoothing</option>
              <option value="arima">ARIMA</option>
            </select>
          </div>
          <button onClick={handleGenerate} disabled={generating} className="btn-primary text-sm">
            {generating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Play className="w-4 h-4 mr-1.5" />}
            Generate Forecast
          </button>
        </div>
      </div>

      {/* Forecast Results */}
      {forecasts.length === 0 && !generating ? (
        <div className="card py-20 text-center">
          <BarChart3 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h3 className="text-base font-medium text-gray-900">No Forecasts Yet</h3>
          <p className="text-sm text-gray-500 mt-1">Configure parameters and click "Generate Forecast" to create predictions.</p>
        </div>
      ) : generating ? (
        <div className="card py-20 text-center">
          <Loader2 className="w-10 h-10 mx-auto text-blue-500 animate-spin mb-3" />
          <p className="text-sm text-gray-500">Generating forecasts...</p>
        </div>
      ) : (
        <>
          {/* Revenue Forecast Chart with Confidence Bands */}
          {chartData.length > 0 && primaryForecast && (
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-base font-semibold text-gray-900">
                    {primaryForecast.account_name || primaryForecast.account_code} Forecast
                  </h2>
                  <p className="text-xs text-gray-500">
                    {primaryForecast.entity_name || primaryForecast.entity_id} &middot; Method: {primaryForecast.method} &middot; Confidence: {Math.round(primaryForecast.confidence * 100)}%
                  </p>
                </div>
                <button
                  onClick={() => handleCompare(primaryForecast.id)}
                  className="btn-secondary text-xs"
                >
                  Compare vs Actuals
                </button>
              </div>
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="period" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                  <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px' }} />
                  <Area type="monotone" dataKey="upper" stroke="none" fill="#dbeafe" fillOpacity={0.5} />
                  <Area type="monotone" dataKey="lower" stroke="none" fill="#ffffff" fillOpacity={1} />
                  <Line type="monotone" dataKey="value" stroke="#2563eb" strokeWidth={2.5} dot={{ fill: '#2563eb', r: 4 }} />
                </AreaChart>
              </ResponsiveContainer>
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                <span className="flex items-center gap-1"><span className="w-3 h-1 bg-blue-600 rounded" /> Forecast</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-100 rounded" /> 95% Confidence Band</span>
              </div>
            </div>
          )}

          {/* Key Metrics Table */}
          <div className="card overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
              <h2 className="text-base font-semibold text-gray-900">Forecast Summary</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="table-header">Entity</th>
                    <th className="table-header">Account</th>
                    <th className="table-header">Method</th>
                    <th className="table-header text-right">Confidence</th>
                    <th className="table-header text-right">Next Period</th>
                    <th className="table-header text-center">Trend</th>
                    <th className="table-header w-24">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {forecasts.map((f) => {
                    const firstVal = f.forecasted_values[0]?.value ?? 0;
                    const lastVal = f.forecasted_values[f.forecasted_values.length - 1]?.value ?? 0;
                    const trend = lastVal - firstVal;

                    return (
                      <tr key={f.id} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
                        <td className="table-cell text-sm font-medium text-gray-900">{f.entity_name || f.entity_id}</td>
                        <td className="table-cell text-sm text-gray-700">{f.account_name || f.account_code}</td>
                        <td className="table-cell">
                          <span className="badge bg-blue-50 text-blue-700 text-xs">{f.method}</span>
                        </td>
                        <td className="table-cell text-right">
                          <span className={clsx(
                            'font-mono text-sm font-medium',
                            f.confidence >= 0.8 ? 'text-green-700' : f.confidence >= 0.5 ? 'text-amber-700' : 'text-red-700',
                          )}>
                            {Math.round(f.confidence * 100)}%
                          </span>
                        </td>
                        <td className="table-cell text-right font-mono text-sm font-medium text-gray-900">
                          {formatAmount(firstVal)}
                        </td>
                        <td className="table-cell text-center">
                          {trend > 0 ? (
                            <ArrowUp className="w-4 h-4 text-green-500 mx-auto" />
                          ) : trend < 0 ? (
                            <ArrowDown className="w-4 h-4 text-red-500 mx-auto" />
                          ) : (
                            <Minus className="w-4 h-4 text-gray-400 mx-auto" />
                          )}
                        </td>
                        <td className="table-cell">
                          <button
                            onClick={() => handleCompare(f.id)}
                            className="text-xs font-medium text-primary-600 hover:text-primary-800 underline"
                          >
                            Compare
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Forecast vs Actuals Comparison */}
          {comparisonData && compChartData.length > 0 && (
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-gray-900">Forecast vs Actuals</h2>
                <span className={clsx(
                  'badge',
                  comparisonData.accuracy_pct >= 90 ? 'bg-green-50 text-green-700' : comparisonData.accuracy_pct >= 70 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700',
                )}>
                  Accuracy: {Number(comparisonData.accuracy_pct).toFixed(1)}%
                </span>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={compChartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="period" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                  <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px' }} />
                  <Legend />
                  <Line type="monotone" dataKey="Forecast" stroke="#3b82f6" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
                  <Bar dataKey="Actual" fill="#10b981" radius={[4, 4, 0, 0]} barSize={20} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
}
